import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  signal,
} from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import { FormControl, Validators } from '@angular/forms';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { MzicSvgComponent, SpinnerComponent } from '@mzic/mzic-components';
import { BusinessRepresentative } from '../../../interfaces/collection.interface';
import { CpfValidator } from '../../../utils/cpf-validator';
import { WaiverCpfFieldComponent } from '../waiver-cpf-field/waiver-cpf-field.component';

export type BusinessRepSelectionStep = 'select-representative' | 'enter-cpf';

export interface BusinessRepresentativeSelection {
  representative: BusinessRepresentative;
  representativeDocumentNumber: string;
}

@Component({
  selector: 'lib-business-rep-selection',
  imports: [
    CommonModule,
    A11yModule,
    TranslocoModule,
    MzicSvgComponent,
    SpinnerComponent,
    WaiverCpfFieldComponent,
  ],
  providers: [
    provideTranslocoScope({
      scope: 'mzic-collection-flow',
      alias: 'lang',
    }),
  ],
  templateUrl: './business-rep-selection.component.html',
  styleUrl: './business-rep-selection.component.scss',
})
export class BusinessRepSelectionComponent {
  @Input() representatives: BusinessRepresentative[] = [];
  @Input() loading = false;
  @Input() submitting = false;
  @Input() businessName = '';
  @Output() representativeSelected =
    new EventEmitter<BusinessRepresentativeSelection>();
  @Output() back = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  readonly selectedRep = signal<BusinessRepresentative | null>(null);
  readonly step = signal<BusinessRepSelectionStep>('select-representative');
  readonly cpfControl = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required],
  });
  readonly titleId = 'business-rep-selection-title';
  readonly descriptionId = 'business-rep-selection-description';

  selectRepresentative(rep: BusinessRepresentative): void {
    this.selectedRep.set(rep);
    this.step.set('select-representative');
    this.cpfControl.reset();
  }

  onConfirm(): void {
    const rep = this.selectedRep();
    if (!rep || this.loading || this.submitting) {
      return;
    }

    if (rep.document_number) {
      this.emitSelection(rep, rep.document_number);
      return;
    }

    if (this.step() === 'select-representative') {
      this.step.set('enter-cpf');
      return;
    }

    if (!this.isValidCpf) {
      this.cpfControl.markAsTouched();
      return;
    }

    this.emitSelection(rep, CpfValidator.unformat(this.cpfControl.value));
  }

  onBack(): void {
    if (this.submitting) {
      return;
    }

    if (this.step() === 'enter-cpf') {
      this.step.set('select-representative');
      return;
    }

    this.back.emit();
  }

  onCancel(): void {
    if (this.submitting) {
      return;
    }

    this.cancel.emit();
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: KeyboardEvent): void {
    event.preventDefault();
    this.onCancel();
  }

  onOverlayClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.onCancel();
    }
  }

  get selectedRepRequiresCpf(): boolean {
    return this.selectedRep()?.document_number === null;
  }

  get isValidCpf(): boolean {
    return CpfValidator.validate(this.cpfControl.value);
  }

  get canConfirm(): boolean {
    if (!this.selectedRep() || this.loading || this.submitting) {
      return false;
    }

    if (this.step() === 'enter-cpf') {
      return this.isValidCpf;
    }

    return true;
  }

  get currentTitleKey(): string {
    return this.step() === 'enter-cpf'
      ? 'lang.business_rep_cpf_title'
      : 'lang.business_rep_title';
  }

  private emitSelection(
    rep: BusinessRepresentative,
    documentNumber: string,
  ): void {
    this.representativeSelected.emit({
      representative: rep,
      representativeDocumentNumber: documentNumber,
    });
  }

  /**
   * Format CPF/document number for display
   */
  formatDocument(documentNumber: string | null): string {
    if (!documentNumber) {
      return '';
    }

    const cleaned = documentNumber.replace(/[^\d]/g, '');
    if (cleaned.length === 11) {
      return CpfValidator.mask(cleaned);
    }
    if (documentNumber.length > 4) {
      return '***' + documentNumber.slice(-4);
    }
    return documentNumber;
  }

  get shimmerItems(): number[] {
    return Array.from({ length: 3 }, (_, i) => i);
  }
}
